import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import * as XLSX from 'xlsx';

const HistorialVentas = () => {
  const [ventas, setVentas] = useState([]);
  const [clientesMap, setClientesMap] = useState({});
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [productoFiltro, setProductoFiltro] = useState('');
  const [ventasFiltradas, setVentasFiltradas] = useState([]);
  const [totalUSD, setTotalUSD] = useState(0);
  const [totalBS, setTotalBS] = useState(0);
  const [totalPendienteUSD, setTotalPendienteUSD] = useState(0);
  const [totalPendienteBS, setTotalPendienteBS] = useState(0);
  const [conteoVentas, setConteoVentas] = useState(0);
  const [rango, setRango] = useState('diario');

  // 1. Obtener todas las ventas y clientes
  useEffect(() => {
    const obtenerDatos = async () => {
      // Clientes
      const clientesSnap = await getDocs(collection(db, 'clientes'));
      const clientes = {};
      clientesSnap.forEach(doc => {
        clientes[doc.id] = doc.data().nombre || doc.id;
      });
      setClientesMap(clientes);

      // Ventas
      const snapshot = await getDocs(collection(db, 'ventas'));
      const datos = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const ordenadas = datos.sort((a, b) => (b.fecha?.seconds || 0) - (a.fecha?.seconds || 0));
      setVentas(ordenadas);
    };
    obtenerDatos();
  }, []);

  // 2. Cambia fechas automáticamente según rango
  useEffect(() => {
    const hoy = new Date();
    let inicio, fin;

    if (rango === 'diario') {
      inicio = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
      fin = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() + 1);
    } else if (rango === 'semanal') {
      const primerDiaSemana = new Date(hoy);
      primerDiaSemana.setDate(hoy.getDate() - hoy.getDay());
      inicio = primerDiaSemana;
      fin = new Date(primerDiaSemana);
      fin.setDate(inicio.getDate() + 7);
    } else if (rango === 'mensual') {
      inicio = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
      fin = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 1);
    } else {
      return;
    }
    const format = d => d.toISOString().split('T')[0];
    setFechaInicio(format(inicio));
    setFechaFin(format(new Date(fin.getTime() - 1000 * 60 * 60 * 24)));
  }, [rango]);

  // 3. Filtrado y cálculo de totales
  useEffect(() => {
    filtrarYCalcular();
    // eslint-disable-next-line
  }, [fechaInicio, fechaFin, productoFiltro, ventas, clientesMap]);

  const filtrarYCalcular = () => {
    const filtradas = ventas.filter((v) => {
      if (!v.fecha) return false;
      const fechaVentaStr = new Date(v.fecha.seconds * 1000).toISOString().split('T')[0];
      const cumpleInicio = !fechaInicio || fechaVentaStr >= fechaInicio;
      const cumpleFin = !fechaFin || fechaVentaStr <= fechaFin;
      const coincideProducto =
        !productoFiltro ||
        v.productos?.some((p) =>
          p.nombre?.toLowerCase().includes(productoFiltro.toLowerCase())
        );
      return cumpleInicio && cumpleFin && coincideProducto;
    });

    let sumaUSD = 0, sumaBS = 0, sumaPendienteUSD = 0, sumaPendienteBS = 0;

    for (const v of filtradas) {
      // Evitar Infinity: solo sumar si monto es numérico y válido
      const montoUSD = isNaN(Number(v.montoUSD)) ? 0 : Number(v.montoUSD);
      const montoBS = isNaN(Number(v.montoBS)) ? 0 : Number(v.montoBS);
      const abonadoUSD = isNaN(Number(v.abonadoUSD)) ? 0 : Number(v.abonadoUSD);
      const abonadoBS = isNaN(Number(v.abonadoBS)) ? 0 : Number(v.abonadoBS);

      // Si la venta fue en BS pero no tiene montoUSD, lo calculamos por tasaBCV si existe
      let usd = montoUSD;
      if ((!usd || usd === 0) && montoBS && v.tasaBCV) {
        usd = Number((montoBS / v.tasaBCV).toFixed(2));
      }
      sumaUSD += usd;
      sumaBS += montoBS;

      let pendienteUSD = usd - abonadoUSD;
      if (pendienteUSD < 0.01) pendienteUSD = 0; // Cerrar decimales menores
      sumaPendienteUSD += pendienteUSD;

      let pendienteBS = montoBS - abonadoBS;
      if (pendienteBS < 0.01) pendienteBS = 0;
      sumaPendienteBS += pendienteBS;
    }

    setVentasFiltradas(filtradas);
    setTotalUSD(sumaUSD);
    setTotalBS(sumaBS);
    setTotalPendienteUSD(sumaPendienteUSD);
    setTotalPendienteBS(sumaPendienteBS);
    setConteoVentas(filtradas.length);
  };

  // 4. Exportar Excel
  const exportarExcel = () => {
    const encabezados = [
      ['Fecha', 'ID', 'Cliente', 'Total USD', 'Abonado USD', 'Pendiente USD',
        'Total BS', 'Abonado BS', 'Pendiente BS', 'Vendedor', 'Productos', 'Estado']
    ];

    const filasData = ventasFiltradas.map((v) => {
      const fechaStr = v.fecha ? new Date(v.fecha.seconds * 1000).toLocaleString() : '';
      const productosStr = v.productos?.map(p => `${p.nombre} x${p.cantidad}`).join(', ') || 'N/A';

      const montoUSD = isNaN(Number(v.montoUSD)) ? 0 : Number(v.montoUSD);
      const montoBS = isNaN(Number(v.montoBS)) ? 0 : Number(v.montoBS);
      const abonadoUSD = isNaN(Number(v.abonadoUSD)) ? 0 : Number(v.abonadoUSD);
      const abonadoBS = isNaN(Number(v.abonadoBS)) ? 0 : Number(v.abonadoBS);

      let usd = montoUSD;
      if ((!usd || usd === 0) && montoBS && v.tasaBCV) {
        usd = Number((montoBS / v.tasaBCV).toFixed(2));
      }

      let pendienteUSD = usd - abonadoUSD;
      if (pendienteUSD < 0.01) pendienteUSD = 0;
      let pendienteBS = montoBS - abonadoBS;
      if (pendienteBS < 0.01) pendienteBS = 0;

      let estado = pendienteUSD === 0 && pendienteBS === 0 ? "Completada" : "Pendiente";

      return [
        fechaStr,
        v.id,
        clientesMap[v.clienteId] || v.clienteId || 'N/A',
        usd.toFixed(2),
        abonadoUSD.toFixed(2),
        pendienteUSD.toFixed(2),
        montoBS.toFixed(2),
        abonadoBS.toFixed(2),
        pendienteBS.toFixed(2),
        v.vendedor || 'Desconocido',
        productosStr,
        estado
      ];
    });

    const hojaDatos = [...encabezados, ...filasData];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(hojaDatos);
    XLSX.utils.book_append_sheet(wb, ws, 'HistorialVentas');
    XLSX.writeFile(wb, 'historial_ventas.xlsx');
  };

  return (
    <div className="reportes-container" style={{ background: "#111", minHeight: "100vh", color: "#f8f8f8", padding: "24px" }}>
      {/* CABECERA */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 18
      }}>
        <h2 style={{ margin: 0, fontWeight: 700, fontSize: "1.5rem", letterSpacing: 0.5 }}>
          Historial de Ventas (Reporte X / Z)
        </h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            style={{
              background: rango === 'diario' ? '#ffd600' : '#282828',
              color: '#222',
              border: 'none',
              borderRadius: 6,
              padding: "8px 18px",
              fontWeight: "bold",
              boxShadow: rango === 'diario' ? '0 0 8px #ffd60055' : undefined
            }}
            onClick={() => setRango('diario')}
          >Diario</button>
          <button
            style={{
              background: rango === 'semanal' ? '#30e3ca' : '#282828',
              color: '#222',
              border: 'none',
              borderRadius: 6,
              padding: "8px 18px",
              fontWeight: "bold",
              boxShadow: rango === 'semanal' ? '0 0 8px #30e3ca55' : undefined
            }}
            onClick={() => setRango('semanal')}
          >Semanal</button>
          <button
            style={{
              background: rango === 'mensual' ? '#f254a4' : '#282828',
              color: '#222',
              border: 'none',
              borderRadius: 6,
              padding: "8px 18px",
              fontWeight: "bold",
              boxShadow: rango === 'mensual' ? '0 0 8px #f254a455' : undefined
            }}
            onClick={() => setRango('mensual')}
          >Mensual</button>
        </div>
      </div>

      {/* FILTROS */}
      <div className="filtros-fechas" style={{ display: 'flex', gap: '16px', marginBottom: '18px', flexWrap: "wrap" }}>
        <div>
          <label>Desde:</label><br />
          <input
            type="date"
            value={fechaInicio}
            onChange={e => setFechaInicio(e.target.value)}
            style={{ background: "#202020", color: "#fff", border: "1px solid #444", borderRadius: "5px", padding: "5px" }}
          />
        </div>
        <div>
          <label>Hasta:</label><br />
          <input
            type="date"
            value={fechaFin}
            onChange={e => setFechaFin(e.target.value)}
            style={{ background: "#202020", color: "#fff", border: "1px solid #444", borderRadius: "5px", padding: "5px" }}
          />
        </div>
        <div>
          <label>Buscar producto:</label><br />
          <input
            type="text"
            placeholder="Producto"
            value={productoFiltro}
            onChange={e => setProductoFiltro(e.target.value)}
            style={{ background: "#202020", color: "#fff", border: "1px solid #444", borderRadius: "5px", padding: "5px" }}
          />
        </div>
        <div>
          <button
            onClick={exportarExcel}
            style={{
              marginTop: '22px',
              padding: '7px 18px',
              borderRadius: '5px',
              backgroundColor: '#00aaff',
              color: 'white',
              border: 'none',
              fontWeight: "bold"
            }}
          >
            Exportar Excel
          </button>
        </div>
      </div>

      {/* Resumen de Totales */}
      <div style={{
        position: 'absolute',
        top: 12,
        right: 22,
        background: '#181818',
        color: '#ffd600',
        padding: '14px',
        borderRadius: '8px',
        minWidth: '220px',
        border: "1px solid #333"
      }}>
        <p style={{ margin: 0, fontWeight: 'bold', color: "#fff" }}>
          Rango: {fechaInicio || '---'} / {fechaFin || '---'}
        </p>
        <p style={{ margin: '8px 0', color: "#ffd600", borderBottom: '1px solid #444', paddingBottom: '7px', fontWeight: 500 }}>
          USD:<br />
          Total: ${totalUSD.toFixed(2)}<br />
          Abonado: ${(totalUSD - totalPendienteUSD).toFixed(2)}<br />
          Pendiente: ${totalPendienteUSD.toFixed(2)}
        </p>
        <p style={{ margin: '8px 0', color: "#30e3ca" }}>
          BS:<br />
          Total: {totalBS.toFixed(2)}<br />
          Abonado: {(totalBS - totalPendienteBS).toFixed(2)}<br />
          Pendiente: {totalPendienteBS.toFixed(2)}
        </p>
        <p style={{ color: "#fff" }}>
          Ventas encontradas: {conteoVentas}
        </p>
      </div>

      {/* Listado de Ventas */}
      {ventasFiltradas.length === 0 ? (
        <p>No hay ventas para ese rango.</p>
      ) : (
        <div className="facturas-lista" style={{ marginTop: '24px', maxWidth: 1000 }}>
          {ventasFiltradas.map((venta) => {
            // Buscar nombre del cliente
            const clienteNombre = clientesMap[venta.clienteId] || venta.clienteId || '---';

            // Calcular valores robustamente
            const montoUSD = isNaN(Number(venta.montoUSD)) ? 0 : Number(venta.montoUSD);
            const montoBS = isNaN(Number(venta.montoBS)) ? 0 : Number(venta.montoBS);
            const abonadoUSD = isNaN(Number(venta.abonadoUSD)) ? 0 : Number(venta.abonadoUSD);
            const abonadoBS = isNaN(Number(venta.abonadoBS)) ? 0 : Number(venta.abonadoBS);

            let usd = montoUSD;
            if ((!usd || usd === 0) && montoBS && venta.tasaBCV) {
              usd = Number((montoBS / venta.tasaBCV).toFixed(2));
            }
            let pendienteUSD = usd - abonadoUSD;
            if (pendienteUSD < 0.01) pendienteUSD = 0;
            let pendienteBS = montoBS - abonadoBS;
            if (pendienteBS < 0.01) pendienteBS = 0;

            // Estado: completada o pendiente
            const completada = pendienteUSD === 0 && pendienteBS === 0;

            return (
              <div key={venta.id} className="factura-item" style={{
                border: '2px solid #222',
                borderRadius: '10px',
                padding: '18px',
                marginBottom: '17px',
                background: '#181818',
                color: "#fff"
              }}>
                <p><strong>ID:</strong> {venta.id}</p>
                <p><strong>Fecha:</strong> {new Date(venta.fecha?.seconds * 1000).toLocaleString()}</p>
                <p><strong>Cliente:</strong> {clienteNombre}</p>
                <div style={{ display: 'flex', gap: '30px', margin: '13px 0', flexWrap: "wrap" }}>
                  <div>
                    <p style={{ color: "#ffd600" }}><strong>USD:</strong></p>
                    <p>Total: ${usd.toFixed(2)}</p>
                    <p>Abonado: ${abonadoUSD.toFixed(2)}</p>
                    <p>Pendiente: ${pendienteUSD.toFixed(2)}</p>
                  </div>
                  <div>
                    <p style={{ color: "#30e3ca" }}><strong>BS:</strong></p>
                    <p>Total: {montoBS.toFixed(2)}</p>
                    <p>Abonado: {abonadoBS.toFixed(2)}</p>
                    <p>Pendiente: {pendienteBS.toFixed(2)}</p>
                  </div>
                </div>
                <p>
                  <strong>Estado: </strong>
                  {completada ? (
                    <span style={{ color: "#30e3ca", fontWeight: 700 }}>Completada</span>
                  ) : (
                    <span style={{ color: "#ffd600", fontWeight: 700 }}>Pendiente</span>
                  )}
                </p>
                <p><strong>Vendedor:</strong> {venta.vendedor || 'Desconocido'}</p>
                <p><strong>Productos:</strong></p>
                <ul style={{ listStyleType: 'none', paddingLeft: '0' }}>
                  {venta.productos?.map((p, idx) => (
                    <li key={idx} style={{ marginBottom: '5px', color: "#ffd600" }}>
                      ▸ {p.nombre} x{p.cantidad} - ${p.precio?.toFixed(2)}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default HistorialVentas;
